/* eslint-disable max-lines */

import { NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormControl,
  UntypedFormControl,
  UntypedFormGroup,
  ReactiveFormsModule,
} from '@angular/forms';

import { Store } from '@ngrx/store';
import { deepEqual } from 'fast-equals';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

import { CurrentUserSelectors, UserSelectors } from '@recruitee/account-data';
import type { CurrentUser, User } from '@recruitee/account-types';
import { FullCandidateSelectors } from '@recruitee/candidate-data';
import { filterBoolean } from '@recruitee/common';
import { TranslatePipe } from '@recruitee/i18n';
import { RoleSelectors } from '@recruitee/roles-data';
import type { Role } from '@recruitee/roles-types';
import {
  HeadingComponent,
  RtTooltip,
  EmptyStateComponent,
  DialogFooterComponent,
  DialogComponentI,
  DialogRef,
  DividerComponent,
  ButtonComponent,
  GroupComponent,
  TabComponent,
  TabsComponent,
  IconButtonComponent,
  IconComponent,
  IconColorDirective,
  InputComponent,
  FormFieldComponent,
  BoxComponent,
  DialogBodyComponent,
  DialogHeaderComponent,
} from '@recruitee/user-interface';
import { includesWithTrim } from '@recruitee/utils';
import type { Visibility } from '@recruitee/visibility-types';

import { RoleWithAdmins } from '../../../types';
import { VisibilityAdminItemComponent } from './visibility-admin-item/visibility-admin-item.component';

export interface VisibilityDialogData {
  assignedAdmins: number[];
  assignedRoles: number[];
  roleSelectionRequired: boolean;
  candidateId?: number;
  offerId?: number;
  groupUsersByAccess?: boolean;
  disabledUserCopy?: string;
}

enum Tab {
  HiringRoles = 'hiring_roles',
  TeamMembers = 'team_members',
}

const maxRenderAdmins = 15;

@Component({
  selector: 'rt-visibility-dialog',
  templateUrl: 'visibility-dialog.component.html',
  styleUrls: ['./visibility-dialog.component.less'],
  standalone: true,
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    BoxComponent,
    FormFieldComponent,
    InputComponent,
    ReactiveFormsModule,
    IconColorDirective,
    IconComponent,
    IconButtonComponent,
    TabsComponent,
    TabComponent,
    GroupComponent,
    ButtonComponent,
    DividerComponent,
    InfiniteScrollModule,
    NgTemplateOutlet,
    HeadingComponent,
    VisibilityAdminItemComponent,
    RtTooltip,
    EmptyStateComponent,
    DialogFooterComponent,
    TranslatePipe,
    UpperCasePipe,
  ],
})
export class VisibilityDialogComponent
  implements OnInit, OnDestroy, DialogComponentI<VisibilityDialogData, Visibility>
{
  public readonly Tab: typeof Tab = Tab;

  public admins: User[] = [];
  public visibleAdmins: User[] = [];
  public disallowedUsers: User[] = [];
  public visibleDisallowedUsers: User[] = [];
  public allAdmins: User[] = [];
  public currentAdmin: CurrentUser;
  public searchControl = new FormControl<string>('', { nonNullable: true });
  public searchQuery: string = '';
  public assignmentsGroup: UntypedFormGroup = new UntypedFormGroup({});
  public roleAssignmentsGroup: UntypedFormGroup = new UntypedFormGroup({});
  public selectedTab: Tab = Tab.TeamMembers;
  public rolesWithAdmins: RoleWithAdmins[] = [];
  public visibleRolesWithAdmins: RoleWithAdmins[] = [];
  public allRolesWithUsers: RoleWithAdmins[] = [];
  public roleSelectionRequired: boolean = true;
  public candidateId?: number | null;
  public offerId?: number;
  public groupUsersByAccess = false;
  public disabledUserCopy: string;

  private assignedAdmins: number[] = [];
  private assignedRoles: number[] = [];
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private cdRef: ChangeDetectorRef,
    private dialog: DialogRef,
  ) {}

  public ngOnInit(): void {
    this.store
      .select(CurrentUserSelectors.data)
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$))
      .subscribe(currentAdmin => {
        this.currentAdmin = currentAdmin;
        this.cdRef.detectChanges();
      });

    combineLatest([this.getUsers(), this.store.select(RoleSelectors.entities())])
      .pipe(
        distinctUntilChanged(deepEqual),
        filter(([admins, roles]) => !!admins && !!roles),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(
        ([admins, roles]: [
          User[] | { usersWithAccess: User[]; usersWithoutAccess: User[] },
          Role[],
        ]) => {
          const groupedAdmins = this.groupedAdminsByRole(
            'usersWithAccess' in admins ? admins.usersWithAccess : admins,
          );

          this.allRolesWithUsers = this.mapAllRolesWithAdmins(roles, groupedAdmins);
          this.allAdmins = this.insertCurrentAdminAtFirstPosition([
            ...('usersWithAccess' in admins ? admins.usersWithAccess : admins),
          ]);
          this.disallowedUsers = 'usersWithoutAccess' in admins ? admins.usersWithoutAccess : [];
          this.allAdmins.forEach(a => this.controlForAdmin(a));
          this.allRolesWithUsers.forEach(a => this.controlForRole(a));
          this.filterRolesAndAdmins(this.searchQuery);
          this.detectChanges();
        },
      );
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public setDialogData(data: VisibilityDialogData): void {
    this.assignedAdmins = data.assignedAdmins;
    this.assignedRoles = data.assignedRoles;
    this.roleSelectionRequired = !!data.roleSelectionRequired;
    this.candidateId = data.candidateId;
    this.groupUsersByAccess = !!data.groupUsersByAccess;
    this.offerId = data.offerId;
    this.disabledUserCopy =
      data.disabledUserCopy || 'app.candidate.admin_select.no_access.tooltip_share_on_the_go';
  }

  public onScroll(): void {
    this.visibleAdmins = this.admins.slice(0, this.visibleAdmins.length + maxRenderAdmins);
    this.visibleRolesWithAdmins = this.visibleRolesWithAdmins.slice(
      0,
      this.visibleRolesWithAdmins.length + maxRenderAdmins,
    );
    const filteredDisallowedUsers = this.filterAdminsByQuery(
      this.disallowedUsers,
      this.searchQuery,
    );
    this.visibleDisallowedUsers = filteredDisallowedUsers.slice(
      0,
      filteredDisallowedUsers.length + maxRenderAdmins,
    );
    this.detectChanges();
  }

  public onSearchQueryChange(query: string): void {
    this.searchQuery = query;
    this.filterRolesAndAdmins(query);
    this.detectChanges();
  }

  public isSearching(): boolean {
    return !!this.searchControl.value && this.searchControl.value.length > 0;
  }

  public cancelSearch(): void {
    this.searchControl.reset('');
    this.onSearchQueryChange('');
  }

  public trackById<T extends { id: number }>(index: number, { id }: T): number {
    return id;
  }

  public controlForAdmin(admin: User): UntypedFormControl {
    if (!this.assignmentsGroup.controls[admin.id]) {
      const defaultValue = this.assignedAdmins.indexOf(admin.id) < 0 ? false : admin.id;
      const formValue = admin.id === this.currentAdmin.id ? admin.id : defaultValue;
      const control = new UntypedFormControl(formValue);
      if (admin.id === this.currentAdmin.id) {
        control.disable();
      }
      this.assignmentsGroup.addControl(admin.id.toString(), control);
    }
    return <UntypedFormControl>this.assignmentsGroup.controls[admin.id];
  }

  public controlForRole(roleWithAdmins: RoleWithAdmins): UntypedFormControl {
    if (!this.roleAssignmentsGroup.controls[roleWithAdmins.id]) {
      const defaultValue =
        this.assignedRoles.indexOf(roleWithAdmins.id) < 0 ? false : roleWithAdmins.id;
      const formValue = roleWithAdmins.isAdmin ? roleWithAdmins.id : defaultValue;
      const control = new UntypedFormControl(formValue);
      if (this.roleSelectionRequired && roleWithAdmins.isAdmin) {
        control.disable();
      }
      this.roleAssignmentsGroup.addControl(roleWithAdmins.id.toString(), control);
    }
    return <UntypedFormControl>this.roleAssignmentsGroup.controls[roleWithAdmins.id];
  }

  public toggleAssignment(admin: User): void {
    if (admin.id === this.currentAdmin.id) {
      return;
    }
    const control = this.assignmentsGroup.controls[admin.id];
    control.setValue(control.value ? false : admin.id);
    control.markAsDirty();
    this.detectChanges();
  }

  public toggleRoleAssignment(roleWithAdmins: RoleWithAdmins): void {
    if (roleWithAdmins.isAdmin) {
      return;
    }
    const control = this.roleAssignmentsGroup.controls[roleWithAdmins.id];
    control.setValue(control.value ? false : roleWithAdmins.id);
    control.markAsDirty();
    this.detectChanges();
  }

  public selectAll(): void {
    if (this.selectedTab === Tab.TeamMembers) {
      this.assignmentsGroup.setValue(
        this.admins.reduce((object, admin) => ({ ...object, [admin.id]: admin.id }), {}),
      );
    } else {
      this.roleAssignmentsGroup.setValue(
        this.rolesWithAdmins.reduce((object, role) => ({ ...object, [role.id]: role.id }), {}),
      );
    }
    this.detectChanges();
  }

  public selectNone(): void {
    if (this.selectedTab === Tab.TeamMembers) {
      this.assignmentsGroup.reset();

      this.allAdmins.forEach(admin => {
        if (admin.id === this.currentAdmin.id) {
          this.assignmentsGroup.controls[admin.id].setValue(admin.id);
        }
      });
    } else {
      this.roleAssignmentsGroup.reset();

      if (this.roleSelectionRequired) {
        this.allRolesWithUsers.forEach(role => {
          if (role.isAdmin) {
            this.roleAssignmentsGroup.controls[role.id].setValue(role.id);
          }
        });
      }
    }
    this.detectChanges();
  }

  public detectChanges(): void {
    this.cdRef.detectChanges();
  }

  public selectTab(selectedTab: Tab): void {
    this.selectedTab = selectedTab;
    this.detectChanges();
  }

  public onCancelClick(): void {
    this.dialog.close();
  }

  public onSaveClick(): void {
    this.dialog.close(this.collectResults());
  }

  public collectResults() {
    return {
      roleIds: Object.values(this.roleAssignmentsGroup.controls)
        .map(control => control.value)
        .filter(v => v),
      level: 'protected',
      adminIds: Object.values(this.assignmentsGroup.controls)
        .map(control => control.value)
        .filter(v => v),
    };
  }

  private groupedAdminsByRole(admins: User[]): Record<string, User[]> {
    return admins.reduce<Record<string, User[]>>(
      (groups, admin) => ({
        ...groups,
        ...{ [admin.roleId]: [...(groups[admin.roleId] || []), admin] },
      }),
      {},
    );
  }

  private mapAllRolesWithAdmins(
    roles: Role[],
    groupedAdmins: Record<string, User[]>,
  ): RoleWithAdmins[] {
    return roles.map(r => ({
      ...r,
      id: r.id!,
      isAdmin: r.admin,
      admins: groupedAdmins[r.id!] || [],
      count: (groupedAdmins[r.id!] && groupedAdmins[r.id!].length) || 0,
    }));
  }

  private filterRolesByQuery(roles: RoleWithAdmins[], searchQuery: string): RoleWithAdmins[] {
    return roles.filter(r => includesWithTrim(r.name, searchQuery));
  }

  private filterAdminsByQuery(admins: User[], searchQuery: string): User[] {
    return admins.filter(a => includesWithTrim(`${a.firstName} ${a.lastName}` || '', searchQuery));
  }

  private insertCurrentAdminAtFirstPosition(admins: User[]): User[] {
    return [
      ...admins.filter(a => a.id === this.currentAdmin.id),
      ...admins.filter(a => a.id !== this.currentAdmin.id),
    ];
  }

  private filterRolesAndAdmins(query: string): void {
    this.admins = this.filterAdminsByQuery(this.allAdmins, query);
    this.rolesWithAdmins = this.filterRolesByQuery(this.allRolesWithUsers, query);
    this.visibleAdmins = this.admins.slice(0, maxRenderAdmins);
    this.visibleRolesWithAdmins = this.rolesWithAdmins.slice(0, maxRenderAdmins);
    this.visibleDisallowedUsers = this.filterAdminsByQuery(this.disallowedUsers, query).slice(
      0,
      maxRenderAdmins,
    );
  }

  private getUsers(): Observable<User[] | { usersWithAccess: User[]; usersWithoutAccess: User[] }> {
    return this.groupUsersByAccess && this.candidateId
      ? this.store.select(FullCandidateSelectors.commonUsersGrouped(this.candidateId))
      : !this.candidateId && this.offerId
        ? this.store.select(FullCandidateSelectors.offerUsersGrouped({ offerId: this.offerId }))
        : this.store.select(UserSelectors.entities());
  }
}
